import moment, { Moment } from 'moment';
import { HYPOTHESIS_STATES } from '../../components/canvas/constants';
import { TEAM_DETAILS_POPOVER_CHART_TEXTS } from '../../constants';

const CHART_TITLE = TEAM_DETAILS_POPOVER_CHART_TEXTS.HYPOTHESIS_METRICS_CHART.TITLE;
const Y_AXIS_TITLE = "Hypotheses Stats";
const X_AXIS_TITLE = "";
const PERIOD_LABEL = "Week ";

export const HYPOTHESIS_STAT_SERIES = {
    ALL: "all",
    UNRESOLVED: "unresolved",
    VALIDATED: "validated",
    INVALIDATED: "invalidated",
};

interface IPeriod {
    start: Moment;
    end: Moment;
}

interface ITeamData {
    hypotheses: CanvasEntry[];
    periods: any[];
    startDate: string;
    endDate: string;
}

const dataOverTime = (type: string, period: IPeriod, teamData: ITeamData) => {
    const data = [];
    const { hypotheses } = teamData;

    if (hypotheses) {
        hypotheses.forEach(hypo => {
            const createdAt = moment(hypo.created);
            const stateModifiedAt = moment(hypo.stateModified);
            const createdInSpecifiedPeriod = moment(createdAt).isBetween(
                period.start,
                period.end,
                undefined,
                "[]"
            );
            const stateModifiedInSpecifiedPeriod = moment(stateModifiedAt).isBetween(
                period.start,
                period.end,
                undefined,
                "[]"
            );

            if (type === HYPOTHESIS_STAT_SERIES.ALL && createdInSpecifiedPeriod) {
                data.push(hypo);
            } else if (
                type === HYPOTHESIS_STAT_SERIES.VALIDATED &&
                hypo.state.name === HYPOTHESIS_STATES.VALIDATED &&
                stateModifiedInSpecifiedPeriod
            ) {
                data.push(hypo);
            } else if (
                type === HYPOTHESIS_STAT_SERIES.INVALIDATED &&
                hypo.state.name === HYPOTHESIS_STATES.INVALIDATED &&
                stateModifiedInSpecifiedPeriod
            ) {
                data.push(hypo);
            } else if (type === HYPOTHESIS_STAT_SERIES.UNRESOLVED) {
                data.push(hypo);
            }
        })
    }

    return data;
};

export const buildHypothesisStatChartByTeam = (teamData: ITeamData) => {
    const { startDate, endDate } = teamData;
    const chartSeries = [];
    const chartCategories = [];
    const periodEndDate = moment(endDate);
    const periodStartDate = moment(startDate);

    chartSeries.push({
        name: "Hypotheses",
        color: "#6889c4",
        fillColor: "#6889c4",
        data: [],
        lastTotal: 0,
        type: "line",
    });

    chartSeries.push({
        name: "Unresolved Hypotheses",
        color: "#f0f0f0",
        fillColor: "#f0f0f0",
        data: [],
        lastTotal: 0,
        type: "area",
        visible: true,
    });

    chartSeries.push({
        name: "Validated Hypotheses",
        color: "#a5d6a7",
        fillColor: "#a5d6a7",
        data: [],
        lastTotal: 0,
        type: "area",
        visible: true,
    });

    chartSeries.push({
        name: "Invalidated Hypotheses",
        color: "#ee918f",
        fillColor: "#ee918f",
        data: [],
        lastTotal: 0,
        type: "area",
        visible: true,
    });

    // Count how many of the three data types occured in between the range
    for (
        let day = periodStartDate, dayIndex = 1;
        day.isSameOrBefore(periodEndDate);
        day.add(1, "days"), dayIndex++
    ) {
        const currentPeriodName = `${PERIOD_LABEL} ${Math.ceil(dayIndex / 7)}`;
        const dateRange: IPeriod = {
            start: periodStartDate,
            end: periodEndDate,
        };
        const allHypotheses = dataOverTime(
            HYPOTHESIS_STAT_SERIES.ALL,
            dateRange,
            teamData
        );
        const unresolvedHypotheses = dataOverTime(
            HYPOTHESIS_STAT_SERIES.UNRESOLVED,
            dateRange,
            teamData
        );
        const validatedHypotheses = dataOverTime(
            HYPOTHESIS_STAT_SERIES.VALIDATED,
            dateRange,
            teamData
        );
        const invalidatedHypotheses = dataOverTime(
            HYPOTHESIS_STAT_SERIES.INVALIDATED,
            dateRange,
            teamData
        );

        const final_Full = allHypotheses.length + chartSeries[0].lastTotal;
        const final_Val = validatedHypotheses.length + chartSeries[2].lastTotal;
        const final_InVal =
            invalidatedHypotheses.length + chartSeries[3].lastTotal;

        const final_max = final_Full - (final_Val + final_InVal);

        let new_statless = unresolvedHypotheses.length + chartSeries[1].lastTotal;

        if (new_statless > final_max) new_statless = final_max;

        chartSeries[0].data.push(final_Full);
        chartSeries[1].data.push(new_statless);
        chartSeries[2].data.push(final_Val);
        chartSeries[3].data.push(final_InVal);

        chartSeries[0].lastTotal += allHypotheses.length;
        chartSeries[1].lastTotal += unresolvedHypotheses.length;
        chartSeries[2].lastTotal += validatedHypotheses.length;
        chartSeries[3].lastTotal += invalidatedHypotheses.length;

        chartCategories.push(currentPeriodName);
    }

    return {
        chart: {
            type: "area",
            height: 300,
        },
        xAxis: {
            categories: chartCategories,
            title: {
                text: X_AXIS_TITLE,
            },
            labels: {
                step: 7,
                style: {
                    fontSize: "15px",
                },
                rotation: -45,
                align: "right",
            },
        },
        legend: {
            enabled: true,
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            area: {
                stacking: "normal",
                lineColor: "#666666",
                lineWidth: 1,
                marker: {
                    lineWidth: 1,
                    lineColor: "#666666",
                },
            },
            series: {
                borderColor: "#ccd6eb",
                dataLabels: {
                    enabled: false,
                },
                marker: {
                    enabled: false,
                },
            },
        },
        tooltip: {
            split: true,
        },
        yAxis: {
            title: {
                text: Y_AXIS_TITLE,
                style: {
                    fontSize: "15px",
                },
            },
            labels: {
                style: {
                    fontSize: "15px",
                },
            },
        },
        title: {
            text: CHART_TITLE,
            style: {
                fontSize: "14px",
            },
        },
        series: chartSeries,
    };
};
